import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Layers, TrendingUp, Package, ClipboardList, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  PageHeader,
  LoadingState,
  MetricCard,
  KPIGrid,
  EquipmentTreeTable,
  flattenTree,
  collectExpandableIds,
  pathToNode,
  findNode,
} from '@/components/common';
import {
  equipmentApi,
  EquipmentTreeNode,
  EquipmentCategory,
  EquipmentStatus,
} from '@/api/services/equipment.api';

// Comprehensive mock data for demo/development
const mockEquipmentTree: EquipmentTreeNode[] = [
  {
    id: 'mock-rig-001',
    companyId: 'mock-company',
    assetNumber: 'RIG-ABC-001',
    name: 'Drilling Rig ABC',
    category: 'DRILLING' as EquipmentCategory,
    status: 'IN_USE' as EquipmentStatus,
    condition: 'GOOD',
    level: 0,
    operatingHours: 12450,
    location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
    _count: { children: 3, workOrders: 12 },
    children: [
      {
        id: 'mock-mudpump-sys',
        companyId: 'mock-company',
        assetNumber: 'MP-SYS-001',
        name: 'Mud Pumps System',
        category: 'DRILLING' as EquipmentCategory,
        status: 'IN_USE' as EquipmentStatus,
        condition: 'GOOD',
        level: 1,
        operatingHours: 8200,
        location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
        _count: { children: 3, workOrders: 5 },
        children: [
          {
            id: 'mock-mp-001',
            companyId: 'mock-company',
            assetNumber: 'MP-001',
            name: 'Mud Pump #1 (Triplex)',
            category: 'DRILLING' as EquipmentCategory,
            status: 'AVAILABLE' as EquipmentStatus,
            condition: 'EXCELLENT',
            level: 2,
            operatingHours: 2800,
            location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
            _count: { children: 0, workOrders: 2 },
            children: [],
          },
          {
            id: 'mock-mp-002',
            companyId: 'mock-company',
            assetNumber: 'MP-002',
            name: 'Mud Pump #2 (Triplex)',
            category: 'DRILLING' as EquipmentCategory,
            status: 'IN_USE' as EquipmentStatus,
            condition: 'GOOD',
            level: 2,
            operatingHours: 3100,
            location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
            _count: { children: 0, workOrders: 1 },
            children: [],
          },
          {
            id: 'mock-mp-003',
            companyId: 'mock-company',
            assetNumber: 'MP-003',
            name: 'Mud Pump #3 (Standby)',
            category: 'DRILLING' as EquipmentCategory,
            status: 'MAINTENANCE' as EquipmentStatus,
            condition: 'FAIR',
            level: 2,
            operatingHours: 2300,
            location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
            _count: { children: 0, workOrders: 2 },
            children: [],
          },
        ],
      },
      {
        id: 'mock-hoist-sys',
        companyId: 'mock-company',
        assetNumber: 'HOIST-SYS-001',
        name: 'Hoisting System',
        category: 'DRILLING' as EquipmentCategory,
        status: 'IN_USE' as EquipmentStatus,
        condition: 'GOOD',
        level: 1,
        operatingHours: 12450,
        location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
        _count: { children: 2, workOrders: 4 },
        children: [
          {
            id: 'mock-drawworks',
            companyId: 'mock-company',
            assetNumber: 'DW-001',
            name: 'Drawworks',
            category: 'DRILLING' as EquipmentCategory,
            status: 'IN_USE' as EquipmentStatus,
            condition: 'GOOD',
            level: 2,
            operatingHours: 12450,
            location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
            _count: { children: 0, workOrders: 2 },
            children: [],
          },
          {
            id: 'mock-crown',
            companyId: 'mock-company',
            assetNumber: 'CB-001',
            name: 'Crown Block',
            category: 'DRILLING' as EquipmentCategory,
            status: 'IN_USE' as EquipmentStatus,
            condition: 'EXCELLENT',
            level: 2,
            operatingHours: 12450,
            location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
            _count: { children: 0, workOrders: 0 },
            children: [],
          },
        ],
      },
      {
        id: 'mock-bop-sys',
        companyId: 'mock-company',
        assetNumber: 'BOP-SYS-001',
        name: 'BOP Stack',
        category: 'SAFETY' as EquipmentCategory,
        status: 'IN_USE' as EquipmentStatus,
        condition: 'EXCELLENT',
        level: 1,
        operatingHours: 1200,
        location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
        _count: { children: 2, workOrders: 3 },
        children: [
          {
            id: 'mock-annular',
            companyId: 'mock-company',
            assetNumber: 'ANN-001',
            name: 'Annular Preventer',
            category: 'SAFETY' as EquipmentCategory,
            status: 'AVAILABLE' as EquipmentStatus,
            condition: 'EXCELLENT',
            level: 2,
            operatingHours: 600,
            location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
            _count: { children: 0, workOrders: 1 },
            children: [],
          },
          {
            id: 'mock-ram',
            companyId: 'mock-company',
            assetNumber: 'RAM-001',
            name: 'Ram Preventers',
            category: 'SAFETY' as EquipmentCategory,
            status: 'AVAILABLE' as EquipmentStatus,
            condition: 'GOOD',
            level: 2,
            operatingHours: 600,
            location: { id: 'loc-1', name: 'Prospect Field Site 1', code: 'PFS1' },
            _count: { children: 0, workOrders: 2 },
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: 'mock-rig-002',
    companyId: 'mock-company',
    assetNumber: 'RIG-XYZ-002',
    name: 'Drilling Rig XYZ',
    category: 'DRILLING' as EquipmentCategory,
    status: 'AVAILABLE' as EquipmentStatus,
    condition: 'GOOD',
    level: 0,
    operatingHours: 8900,
    location: { id: 'loc-2', name: 'Prospect Field Site 2', code: 'PFS2' },
    _count: { children: 2, workOrders: 3 },
    children: [
      {
        id: 'mock-rotary-sys',
        companyId: 'mock-company',
        assetNumber: 'ROT-SYS-001',
        name: 'Rotary System',
        category: 'DRILLING' as EquipmentCategory,
        status: 'AVAILABLE' as EquipmentStatus,
        condition: 'GOOD',
        level: 1,
        operatingHours: 8900,
        location: { id: 'loc-2', name: 'Prospect Field Site 2', code: 'PFS2' },
        _count: { children: 2, workOrders: 1 },
        children: [
          {
            id: 'mock-rotary-table',
            companyId: 'mock-company',
            assetNumber: 'RT-001',
            name: 'Rotary Table',
            category: 'DRILLING' as EquipmentCategory,
            status: 'AVAILABLE' as EquipmentStatus,
            condition: 'GOOD',
            level: 2,
            operatingHours: 8900,
            location: { id: 'loc-2', name: 'Prospect Field Site 2', code: 'PFS2' },
            _count: { children: 0, workOrders: 0 },
            children: [],
          },
          {
            id: 'mock-top-drive',
            companyId: 'mock-company',
            assetNumber: 'TD-001',
            name: 'Top Drive System',
            category: 'DRILLING' as EquipmentCategory,
            status: 'REPAIR' as EquipmentStatus,
            condition: 'POOR',
            level: 2,
            operatingHours: 8900,
            location: { id: 'loc-2', name: 'Prospect Field Site 2', code: 'PFS2' },
            _count: { children: 0, workOrders: 1 },
            children: [],
          },
        ],
      },
      {
        id: 'mock-circulating-sys',
        companyId: 'mock-company',
        assetNumber: 'CIRC-SYS-001',
        name: 'Circulating System',
        category: 'DRILLING' as EquipmentCategory,
        status: 'AVAILABLE' as EquipmentStatus,
        condition: 'GOOD',
        level: 1,
        operatingHours: 8500,
        location: { id: 'loc-2', name: 'Prospect Field Site 2', code: 'PFS2' },
        _count: { children: 0, workOrders: 2 },
        children: [],
      },
    ],
  },
  {
    id: 'mock-comp-001',
    companyId: 'mock-company',
    assetNumber: 'COMP-UNIT-001',
    name: 'Completion Unit Alpha',
    category: 'COMPLETION' as EquipmentCategory,
    status: 'MAINTENANCE' as EquipmentStatus,
    condition: 'FAIR',
    level: 0,
    operatingHours: 5600,
    location: { id: 'loc-3', name: 'Completion Yard', code: 'CY1' },
    _count: { children: 1, workOrders: 2 },
    children: [
      {
        id: 'mock-wireline',
        companyId: 'mock-company',
        assetNumber: 'WL-001',
        name: 'Wireline Unit',
        category: 'COMPLETION' as EquipmentCategory,
        status: 'MAINTENANCE' as EquipmentStatus,
        condition: 'FAIR',
        level: 1,
        operatingHours: 3200,
        location: { id: 'loc-3', name: 'Completion Yard', code: 'CY1' },
        _count: { children: 0, workOrders: 1 },
        children: [],
      },
    ],
  },
];

export default function EquipmentHierarchy() {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<EquipmentTreeNode[]>([]);
  const [filteredEquipment, setFilteredEquipment] = useState<EquipmentTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<EquipmentCategory | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<EquipmentStatus | 'ALL'>('ALL');
  // Expansion lives here, not inside the rows: filters need to drive it, and
  // expand/collapse all needs something to act on.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Focus re-roots the table at one subtree so deep branches stay readable.
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const loadEquipmentTree = useCallback(async () => {
    try {
      setLoading(true);
      const response = await equipmentApi.getTree();
      // Ensure all nodes have a children array
      const normalizeNode = (node: any): EquipmentTreeNode => ({
        ...node,
        children: node.children ? node.children.map(normalizeNode) : [],
      });

      const treeData = response.data.map(normalizeNode);

      // If no data from API, use comprehensive mock data for demo
      if (treeData.length === 0) {
        setEquipment(mockEquipmentTree);
        toast.info('Displaying sample equipment hierarchy data');
      } else {
        setEquipment(treeData);
      }
    } catch (error: any) {
      console.error('Error loading equipment tree:', error);
      // On error, show mock data for better UX
      setEquipment(mockEquipmentTree);
      toast.warning('Using sample data - API connection unavailable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEquipmentTree();
  }, [loadEquipmentTree]);
  const applyFilters = useCallback(() => {
    if (categoryFilter === 'ALL' && statusFilter === 'ALL') {
      setFilteredEquipment(equipment);
      return;
    }

    const filterNode = (node: EquipmentTreeNode): EquipmentTreeNode | null => {
      if (!node) return null;

      const matchesCategory = categoryFilter === 'ALL' || node.category === categoryFilter;
      const matchesStatus = statusFilter === 'ALL' || node.status === statusFilter;
      const children = node.children || [];

      if (matchesCategory && matchesStatus) {
        // This node matches, include it with all its children
        return {
          ...node,
          children: children.map(filterNode).filter((n): n is EquipmentTreeNode => n !== null),
        };
      }

      // This node doesn't match, but check if any children match
      const filteredChildren = children
        .map(filterNode)
        .filter((n): n is EquipmentTreeNode => n !== null);

      if (filteredChildren.length > 0) {
        // Include this node because it has matching children
        return {
          ...node,
          children: filteredChildren,
        };
      }

      return null;
    };

    const filtered = equipment.map(filterNode).filter((n): n is EquipmentTreeNode => n !== null);
    setFilteredEquipment(filtered);
    // A filter keeps ancestors of every match, so those ancestors would
    // otherwise sit there collapsed with no visible reason for being listed.
    setExpanded(new Set(collectExpandableIds(filtered)));
  }, [categoryFilter, equipment, statusFilter]);

  useEffect(() => {
    applyFilters();
  }, [equipment, categoryFilter, statusFilter, applyFilters]);

  const handleAddEquipment = () => {
    navigate('/equipment/new');
  };

  const handleClearFilters = () => {
    setCategoryFilter('ALL');
    setStatusFilter('ALL');
  };

  const hasActiveFilters = categoryFilter !== 'ALL' || statusFilter !== 'ALL';

  // What the table is rooted at: the whole filtered forest, or one subtree.
  const focusedNode = focusedId ? findNode(filteredEquipment, focusedId) : null;
  const rootNodes = focusedNode ? [focusedNode] : filteredEquipment;
  const breadcrumb = focusedId ? (pathToNode(filteredEquipment, focusedId) ?? []) : [];
  const rows = flattenTree(rootNodes, expanded);
  const expandableIds = collectExpandableIds(rootNodes);
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));

  const toggleNode = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const focusNode = (node: EquipmentTreeNode) => {
    setFocusedId(node.id);
    // Focusing implies wanting to see inside it.
    setExpanded((prev) => new Set(prev).add(node.id));
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading equipment hierarchy...</p>
        </div>
        <LoadingState variant="list" count={6} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <PageHeader
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Equipment', href: '/equipment' },
          { label: 'Hierarchy' },
        ]}
        title="Equipment Hierarchy"
        description="Assets arranged by parent and child, from rigs at the top through subsystems to components."
        actions={
          <Button onClick={handleAddEquipment}>
            <Plus className="h-4 w-4 mr-2" />
            Add Equipment
          </Button>
        }
      />

      {/* Summary Stats */}
      <KPIGrid columns={4}>
        <MetricCard
          label="Total Equipment"
          value={countNodes(equipment)}
          icon={<Package className="h-5 w-5" />}
          sentiment="info"
          trend={{ direction: 'neutral', value: 'All hierarchy' }}
        />
        <MetricCard
          label="Root Equipment"
          value={equipment.length}
          icon={<Layers className="h-5 w-5" />}
          sentiment="info"
          trend={{ direction: 'neutral', value: 'Level 0' }}
        />
        <MetricCard
          label="Active Work Orders"
          value={countWorkOrders(equipment)}
          icon={<ClipboardList className="h-5 w-5" />}
          sentiment={countWorkOrders(equipment) > 0 ? 'warning' : 'success'}
          trend={{ direction: 'neutral', value: 'All equipment' }}
        />
        <MetricCard
          label="Filtered View"
          value={countNodes(filteredEquipment)}
          icon={<TrendingUp className="h-5 w-5" />}
          sentiment="neutral"
          trend={{ direction: 'neutral', value: hasActiveFilters ? 'Filtered' : 'No filters' }}
        />
      </KPIGrid>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select
          value={categoryFilter}
          onValueChange={(value) => setCategoryFilter(value as EquipmentCategory | 'ALL')}
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Categories</SelectItem>
            <SelectItem value="DRILLING">Drilling</SelectItem>
            <SelectItem value="COMPLETION">Completion</SelectItem>
            <SelectItem value="PRODUCTION">Production</SelectItem>
            <SelectItem value="TESTING">Testing</SelectItem>
            <SelectItem value="SAFETY">Safety</SelectItem>
            <SelectItem value="TRANSPORTATION">Transportation</SelectItem>
            <SelectItem value="OTHER">Other</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as EquipmentStatus | 'ALL')}
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Status</SelectItem>
            <SelectItem value="AVAILABLE">Available</SelectItem>
            <SelectItem value="IN_USE">In Use</SelectItem>
            <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
            <SelectItem value="REPAIR">Repair</SelectItem>
            <SelectItem value="RETIRED">Retired</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        )}
      </div>

      {/* Breadcrumb — only meaningful once the view is focused on a subtree */}
      {focusedNode && (
        <nav aria-label="Hierarchy path" className="flex flex-wrap items-center gap-1 text-sm">
          <Button variant="ghost" size="sm" onClick={() => setFocusedId(null)}>
            All equipment
          </Button>
          {breadcrumb.map((node) => (
            <span key={node.id} className="flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              <Button variant="ghost" size="sm" onClick={() => setFocusedId(node.id)}>
                {node.name}
              </Button>
            </span>
          ))}
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <span className="px-2 font-semibold text-foreground">{focusedNode.name}</span>
        </nav>
      )}

      {/* Tree controls */}
      {rows.length > 0 && expandableIds.length > 0 && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Showing {rows.length} of {countNodes(rootNodes)} assets · click a row to open it
            {expandableIds.length > 0 && ', Focus to drill into a subtree'}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded(allExpanded ? new Set() : new Set(expandableIds))}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </div>
      )}

      {/* Equipment Hierarchy */}
      {rows.length === 0 ? (
        <Card className="p-12 text-center">
          <h3 className="mb-2 text-base font-semibold text-foreground">No Equipment Found</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            {hasActiveFilters
              ? 'No equipment matches your current filters. Try adjusting or clearing them.'
              : 'Get started by adding your first piece of equipment.'}
          </p>
          {hasActiveFilters && (
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </Card>
      ) : (
        <EquipmentTreeTable
          rows={rows}
          expanded={expanded}
          onToggle={toggleNode}
          onOpen={(node) => navigate(`/equipment/${node.id}`)}
          onFocus={focusNode}
          focusedId={focusedId}
        />
      )}
    </div>
  );
}

// Helper function to count total nodes in the tree
function countNodes(nodes: EquipmentTreeNode[]): number {
  if (!nodes || nodes.length === 0) return 0;
  let count = nodes.length;
  for (const node of nodes) {
    count += countNodes(node.children);
  }
  return count;
}

// Helper function to count total work orders
function countWorkOrders(nodes: EquipmentTreeNode[]): number {
  if (!nodes || nodes.length === 0) return 0;
  let count = 0;
  for (const node of nodes) {
    count += node._count?.workOrders || 0;
    count += countWorkOrders(node.children);
  }
  return count;
}
